import TourAvailabilityCalendar from '@/components/TourAvailabilityCalendar'
import { TourInstance } from '@/types'
import { motion } from 'framer-motion'
import { Calendar, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface TourCalendarModalProps {
  isOpen: boolean
  onClose: () => void
  prefersReducedMotion?: boolean
}

const TourCalendarModal: React.FC<TourCalendarModalProps> = ({
  isOpen,
  onClose,
  prefersReducedMotion = false
}) => {
  const navigate = useNavigate()
  const [selectedTour, setSelectedTour] = useState<TourInstance | null>(null)

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden' // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const handleTourSelect = (tourInstance: TourInstance) => {
    setSelectedTour(tourInstance)
  }

  const handleBookNow = () => {
    if (selectedTour) {
      // Navigate to contact page with preselected tour
      navigate(`/contact?tour=${selectedTour.id}&date=${selectedTour.date}`)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: prefersReducedMotion ? 0.2 : 0.3 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{
            duration: prefersReducedMotion ? 0.2 : 0.4,
            ease: [0.4, 0, 0.2, 1]
          }}
          className="bg-garage-card rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden pointer-events-auto relative border-2 border-garage-border"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-garage-primary to-garage-accent p-6 border-b-2 border-garage-accent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-garage-cream/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-garage-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-garage-cream font-garage-title">
                    Select Your Adventure
                  </h2>
                  <p className="text-garage-cream/80 text-sm mt-1">
                    Choose from available tour dates and book instantly
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-garage-cream/10 rounded-lg transition-colors group"
                aria-label="Close modal"
              >
                <X className="h-6 w-6 text-garage-cream group-hover:text-garage-red transition-colors" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            <TourAvailabilityCalendar
              selectedTourId={selectedTour?.id}
              onTourSelect={handleTourSelect}
              className="mb-0"
            />
          </div>

          {/* Footer with Book Now Button */}
          {selectedTour && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t-2 border-garage-border bg-garage-surface p-6"
            >
              <div className="flex items-center justify-between gap-6">
                <div className="flex-1">
                  <h3 className="font-bold text-garage-dark text-lg font-garage-title">
                    {selectedTour.tour_schedule?.tour?.title}
                  </h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-garage-text">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-garage-accent" />
                      {new Date(selectedTour.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="text-garage-accent font-semibold">
                      ${selectedTour.price} per person
                    </span>
                    <span className="text-garage-text/70">
                      {selectedTour.max_capacity - selectedTour.current_bookings} spots left
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleBookNow}
                  className="btn-garage-primary px-8 py-4 text-lg font-bold flex items-center gap-2 hover:scale-105 transition-transform"
                >
                  <Calendar className="h-5 w-5" />
                  Book This Tour
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </>
  )
}

export default TourCalendarModal
